import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import {
    Container,
    Navbar,
} from "react-bootstrap";
import {
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Button,
    Divider,
    Grid,
    Avatar,
    useTheme,
    alpha,
} from "@mui/material";
import {
    ArrowBackOutlined,
    AccountBalanceWallet,
    TrendingUp,
    Redeem,
    Receipt,
    Stars,
    CalendarToday,
} from "@mui/icons-material";
import { t } from "i18next";
import {
    setMobileBackColor,
    setMobileCardColor,
    setMobileTextColor,
    setMobileCardBorderColor,
    setMobileButttonBackColor,
    getLocalStorageMenuItem,
    setLocalStorageMenuItem,
} from "../utility";
import coverSample from "../images/No_Image_Available.jpg";
import no_image from "../images/No_Image_Available.jpg";
import Toast from "../common/toast";
import Loading from "../common/loading";
import BottomNavBar from "../components/BottomNavBar";
import "../css/mobileScreens.css";

const MyPoints = ({ tableQr, tableName }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const queryString = window.location.search;
    const urlParam = new URLSearchParams(queryString);

    const [loading, setLoading] = useState(false);
    const [toastSettings, setToastSettings] = useState({
        open: false,
        message: "",
        success: false,
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    const mobileLoginToken = getLocalStorageMenuItem('mobileLoginToken');
    const [allTreeData, setAllTreeData] = useState(() => {
        const savedTreeData = getLocalStorageMenuItem("tree_data");
        return savedTreeData ? savedTreeData : [];
    });

    const venueId = allTreeData?.details?.venue_id || getLocalStorageMenuItem('venueId');

    const lang = getLocalStorageMenuItem("lang");

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 768);
            setIsTablet(width >= 768 && width < 1024);
            setIsDesktop(width >= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleOpenToast = (message, state) => {
        setToastSettings({
            open: true,
            success: state,
            message: message
        });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setToastSettings({ open: false, message: "", success: false });
    };

    // Fetch loyalty points balance
    const { data: pointsData, isLoading: isLoadingPoints } = useQuery(
        ["loyalty-points", venueId],
        async () => {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/customer/loyalty-points?venue_id=${venueId}`,
                {
                    headers: {
                        Authorization: `Bearer ${mobileLoginToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            console.log("response loyalty", response);
            return response.data.data;
        },
        {
            enabled: !!mobileLoginToken && !!venueId,
            refetchOnWindowFocus: false,
            onError: (error) => {
                handleOpenToast(
                    <div className="alignByLang">
                        {t("Error fetching loyalty points: ") + (error.response?.data?.message || error.message)}
                    </div>,
                    false
                );
            },
        }
    );

    // Fetch loyalty transactions
    const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery(
        ["loyalty-transactions", venueId],
        async () => {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/customer/loyalty-transactions?venue_id=${venueId}`,
                {
                    headers: {
                        Authorization: `Bearer ${mobileLoginToken}`,
                        "Content-Type": "application/json",
                        timeZone: getLocalStorageMenuItem("timezone") || "",
                    },
                }
            );
            return response.data.data;
        },
        {
            enabled: !!mobileLoginToken && !!venueId,
            refetchOnWindowFocus: false,
            onError: (error) => {
                handleOpenToast(
                    <div className="alignByLang">
                        {t("Error fetching transactions: ") + (error.response?.data?.message || error.message)}
                    </div>,
                    false
                );
            },
        }
    );

    // Fetch public details for header
    const { data: publicData, isLoading: isLoadingPublic } = useQuery(
        "public-details",
        async () => {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/menus/${tableQr}/public-details`,
                {
                    headers: {
                        localization: lang,
                        timeZone: getLocalStorageMenuItem("timezone") || ""
                    }
                }
            );
            return response.data.data;
        }
    );

    const handleBack = () => {
        if (urlParam.get("section")) {
            navigate(
                `/${tableName}/${tableQr}/sections?section=${urlParam.get("section")}&t=${urlParam.get("t")}&lang=${lang}`,
                { replace: true }
            );
        } else {
            navigate(
                `/${tableName}/${tableQr}/sections?section=0&t=${urlParam.get("t")}&lang=${lang}`,
                { replace: true }
            );
        }
    };

    const handleBackHome = () => {
        navigate(
            `/${tableName}/${tableQr}/sections?section=0&t=${urlParam.get("t")}&lang=${lang}`,
            { replace: true }
        );
    };

    const onImageError = (e) => {
        e.target.src = coverSample;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getTransactionIcon = (type) => {
        return type === "earn" ? (
            <TrendingUp sx={{ color: theme.palette.success.main }} />
        ) : (
            <Redeem sx={{ color: theme.palette.error.main }} />
        );
    };

    const getTransactionColor = (type) => {
        return type === "earn" ? theme.palette.success.main : theme.palette.error.main;
    };

    if (isLoadingPoints || isLoadingTransactions || isLoadingPublic || loading) {
        return <Loading />;
    }

    return (
        <div
            className="min-vh-100 alignByLang"
            style={{ backgroundColor: setMobileBackColor() }}
        >
            <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Container fluid>
                <div style={{ marginBottom: 70 }} className="row">
                    <div className={`${isDesktop ? '' : 'col-lg-4'} ms-auto p-0`}>
                        <div className={`${isDesktop ? 'container' : ''} mobile-side-screen`}>
                            {/* Header Section */}
                            <div
                                className={isDesktop ? `slider-desktop-image` : `slider-mobile-image`}
                                style={{
                                    position: 'relative',
                                    borderBottomLeftRadius: '20px',
                                    borderBottomRightRadius: '20px',
                                    height: 250
                                }}
                            >
                                <img
                                    src={publicData?.cover || coverSample}
                                    onError={onImageError}
                                    className="slider-mobile-image"
                                    alt="cover"
                                />
                                <Navbar
                                    className="d-flex justify-content-between align-items-center mobile-top-nav"
                                    style={{ backgroundColor: setMobileBackColor() }}
                                >
                                    <Button
                                        variant="text"
                                        className="ms-2 dark-color cursor"
                                        style={{ color: setMobileTextColor() }}
                                        onClick={handleBack}
                                    >
                                        <ArrowBackOutlined sx={{ color: setMobileTextColor() }} />
                                    </Button>

                                    <Navbar.Brand className="text-center">
                                        <img
                                            src={publicData?.logo || no_image}
                                            className="me-4 slider-mobile-logo cursor"
                                            onError={onImageError}
                                            onClick={handleBackHome}
                                            alt="logo"
                                        />
                                    </Navbar.Brand>
                                </Navbar>
                            </div>

                            {/* Content Section */}
                            <div className="sections">
                                <div
                                    className="sections-content"
                                    style={{ marginTop: "54px", marginBottom: "24px", padding: "0 16px" }}
                                >
                                    {/* Page Title */}
                                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                                        <Typography
                                            variant="h5"
                                            className="head-font"
                                            sx={{
                                                color: setMobileTextColor(),
                                                fontWeight: 'bold',
                                                mb: 1
                                            }}
                                        >
                                            {t("My Loyalty Points")}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            className="sub-font"
                                            sx={{ color: alpha(setMobileTextColor(), 0.7) }}
                                        >
                                            {t("Track your rewards and transactions")}
                                        </Typography>
                                    </Box>

                                    {/* Balance Cards */}
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {/* Current Balance Card */}
                                        <Grid item xs={12}>
                                            <Card
                                                sx={{
                                                    backgroundColor: setMobileCardColor(),
                                                    border: `1px solid ${setMobileCardBorderColor()}`,
                                                    borderRadius: '16px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                    background: `linear-gradient(135deg, ${setMobileButttonBackColor()}15 0%, ${setMobileCardColor()} 100%)`,
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: setMobileButttonBackColor(),
                                                                width: 56,
                                                                height: 56,
                                                                mr: 2
                                                            }}
                                                        >
                                                            <Stars fontSize="large" />
                                                        </Avatar>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="body2"
                                                                className="sub-font"
                                                                sx={{ color: alpha(setMobileTextColor(), 0.7), mb: 0.5 }}
                                                            >
                                                                {t("Current Balance")}
                                                            </Typography>
                                                            <Typography
                                                                variant="h3"
                                                                className="head-font"
                                                                sx={{
                                                                    color: setMobileTextColor(),
                                                                    fontWeight: 'bold',
                                                                    ml: lang === "ar" || lang == "fr" ? 0 : 2, mr: lang === "ar" || lang == "fr" ? 2 : 0
                                                                }}
                                                            >
                                                                {pointsData?.balance || 0}
                                                                <Typography
                                                                    component="span"
                                                                    variant="h6"
                                                                    sx={{ ml: lang === "ar" || lang == "fr" ? 0 : 2, mr: lang === "ar" || lang == "fr" ? 2 : 0, color: alpha(setMobileTextColor(), 0.6) }}
                                                                >
                                                                    {t("pts")}
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {pointsData?.program && (
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                pt: 2,
                                                                borderTop: `1px solid ${alpha(setMobileCardBorderColor(), 0.5)}`
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                className="sub-font"
                                                                sx={{ color: alpha(setMobileTextColor(), 0.7) }}
                                                            >
                                                                {t("Worth approximately")}{" "}
                                                                <strong style={{ color: setMobileButttonBackColor() }}>
                                                                    {(pointsData.balance * pointsData.program.currency_per_point).toFixed(2)}
                                                                </strong>
                                                                {" "}{t("in rewards")}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Statistics Cards */}
                                        <Grid item xs={6}>
                                            <Card
                                                sx={{
                                                    backgroundColor: setMobileCardColor(),
                                                    border: `1px solid ${setMobileCardBorderColor()}`,
                                                    borderRadius: '16px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    height: '100%'
                                                }}
                                            >
                                                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                                            width: 48,
                                                            height: 48,
                                                            mx: 'auto',
                                                            mb: 1.5
                                                        }}
                                                    >
                                                        <TrendingUp sx={{ color: theme.palette.success.main }} />
                                                    </Avatar>
                                                    <Typography
                                                        variant="h5"
                                                        className="head-font"
                                                        sx={{
                                                            color: setMobileTextColor(),
                                                            fontWeight: 'bold',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {pointsData?.total_earned || 0}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        className="sub-font"
                                                        sx={{ color: alpha(setMobileTextColor(), 0.6) }}
                                                    >
                                                        {t("Total Earned")}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Card
                                                sx={{
                                                    backgroundColor: setMobileCardColor(),
                                                    border: `1px solid ${setMobileCardBorderColor()}`,
                                                    borderRadius: '16px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    height: '100%'
                                                }}
                                            >
                                                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                                            width: 48,
                                                            height: 48,
                                                            mx: 'auto',
                                                            mb: 1.5
                                                        }}
                                                    >
                                                        <Redeem sx={{ color: theme.palette.error.main }} />
                                                    </Avatar>
                                                    <Typography
                                                        variant="h5"
                                                        className="head-font"
                                                        sx={{
                                                            color: setMobileTextColor(),
                                                            fontWeight: 'bold',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {pointsData?.total_redeemed || 0}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        className="sub-font"
                                                        sx={{ color: alpha(setMobileTextColor(), 0.6) }}
                                                    >
                                                        {t("Total Redeemed")}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    {/* Program Info Card */}
                                    {pointsData?.program && (
                                        <Card
                                            sx={{
                                                backgroundColor: setMobileCardColor(),
                                                border: `1px solid ${setMobileCardBorderColor()}`,
                                                borderRadius: '16px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                mb: 3
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    className="head-font"
                                                    sx={{
                                                        color: setMobileTextColor(),
                                                        fontWeight: 'bold',
                                                        mb: 2
                                                    }}
                                                >
                                                    {t("Program Details")}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography
                                                            variant="body2"
                                                            className="sub-font"
                                                            sx={{ color: alpha(setMobileTextColor(), 0.7) }}
                                                        >
                                                            {t("Points per currency")}
                                                        </Typography>
                                                        <Chip
                                                            label={pointsData.program.points_per_currency}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(setMobileButttonBackColor(), 0.1),
                                                                color: setMobileButttonBackColor(),
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography
                                                            variant="body2"
                                                            className="sub-font"
                                                            sx={{ color: alpha(setMobileTextColor(), 0.7) }}
                                                        >
                                                            {t("Minimum redeem points")}
                                                        </Typography>
                                                        <Chip
                                                            label={pointsData.program.min_redeem_points}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(setMobileButttonBackColor(), 0.1),
                                                                color: setMobileButttonBackColor(),
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography
                                                            variant="body2"
                                                            className="sub-font"
                                                            sx={{ color: alpha(setMobileTextColor(), 0.7) }}
                                                        >
                                                            {t("Max redeem percentage")}
                                                        </Typography>
                                                        <Chip
                                                            label={`${pointsData.program.max_redeem_percentage}%`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(setMobileButttonBackColor(), 0.1),
                                                                color: setMobileButttonBackColor(),
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Transaction History */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="h6"
                                            className="head-font"
                                            sx={{
                                                color: setMobileTextColor(),
                                                fontWeight: 'bold',
                                                mb: 2
                                            }}
                                        >
                                            {t("Transaction History")}
                                        </Typography>

                                        {transactionsData && transactionsData.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {transactionsData.map((transaction) => (
                                                    <Card
                                                        key={transaction.id}
                                                        sx={{
                                                            backgroundColor: setMobileCardColor(),
                                                            border: `1px solid ${setMobileCardBorderColor()}`,
                                                            borderRadius: '12px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                            transition: 'transform 0.2s',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                            }
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                                <Avatar
                                                                    sx={{
                                                                        bgcolor: alpha(getTransactionColor(transaction.type), 0.1),
                                                                        width: 40,
                                                                        height: 40
                                                                    }}
                                                                >
                                                                    {getTransactionIcon(transaction.type)}
                                                                </Avatar>

                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                                        <Typography
                                                                            variant="body1"
                                                                            className="head-font"
                                                                            sx={{
                                                                                color: setMobileTextColor(),
                                                                                fontWeight: 'bold',
                                                                                flex: 1
                                                                            }}
                                                                        >
                                                                            {transaction.description}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={`${transaction.type === 'earn' ? '+' : '-'}${transaction.points}`}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: alpha(getTransactionColor(transaction.type), 0.1),
                                                                                color: getTransactionColor(transaction.type),
                                                                                fontWeight: 'bold',
                                                                                ml: 1
                                                                            }}
                                                                        />
                                                                    </Box>

                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        {transaction.order_id && (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Receipt sx={{ fontSize: 14, color: alpha(setMobileTextColor(), 0.5) }} />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    className="sub-font"
                                                                                    sx={{ color: alpha(setMobileTextColor(), 0.6) }}
                                                                                >
                                                                                    {t("Order")} #{transaction.order_id}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}

                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <CalendarToday sx={{ fontSize: 14, color: alpha(setMobileTextColor(), 0.5) }} />
                                                                            <Typography
                                                                                variant="caption"
                                                                                className="sub-font"
                                                                                sx={{ color: alpha(setMobileTextColor(), 0.6) }}
                                                                            >
                                                                                {formatDate(transaction.created_at)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>

                                                                    {transaction.expires_at && transaction.type === 'earn' && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            className="sub-font"
                                                                            sx={{
                                                                                color: theme.palette.warning.main,
                                                                                mt: 0.5,
                                                                                display: 'block'
                                                                            }}
                                                                        >
                                                                            {t("Expires")}: {formatDate(transaction.expires_at)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Card
                                                sx={{
                                                    backgroundColor: setMobileCardColor(),
                                                    border: `1px solid ${setMobileCardBorderColor()}`,
                                                    borderRadius: '16px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                }}
                                            >
                                                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                                    <AccountBalanceWallet
                                                        sx={{
                                                            fontSize: 64,
                                                            color: alpha(setMobileTextColor(), 0.3),
                                                            mb: 2
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body1"
                                                        className="sub-font"
                                                        sx={{ color: alpha(setMobileTextColor(), 0.6) }}
                                                    >
                                                        {t("No transactions yet")}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        className="sub-font"
                                                        sx={{ color: alpha(setMobileTextColor(), 0.5) }}
                                                    >
                                                        {t("Start earning points with your orders!")}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>

            <Toast
                open={toastSettings.open}
                message={toastSettings.message}
                resetToast={handleCloseToast}
                success={toastSettings.success}
            />

            <BottomNavBar qr={tableQr} qrName={tableName} />
        </div>
    );
};

export default MyPoints;